import { extractAccountReferences, resolveId } from "@medplum/core";
import type { ChargeItem, Provenance, ProvenanceAgent, Reference } from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";

import {
  CHARGE_ITEM_PROVENANCE_ACTIVITY_DISPLAY,
  CHARGE_ITEM_PROVENANCE_ACTIVITY_SYSTEM,
  ChargeItemProvenanceActivityCode,
} from "../constants/charge-item-provenance";

import {
  buildVersionedTargetProvenance,
  getProvenanceActivityCode,
  getProvenanceTargetVersionId,
} from "./versioned-target-provenance";

export interface BuildChargeItemProvenanceParams {
  /** The ChargeItem as it exists after the write, carrying the version this Provenance describes. */
  chargeItem: ChargeItem;
  activityCode: ChargeItemProvenanceActivityCode;
  /** Who made the change — the acting user's profile. */
  agentReference: ProvenanceAgent["who"];
  /**
   * Practice to account the Provenance to when the ChargeItem does not yet carry accounts.
   */
  practiceOrganizationId?: string;
}

/**
 * Builds the Provenance that records one service mutation.
 *
 * @param chargeItem The ChargeItem after the write, whose `meta.versionId` is stamped on the target.
 * @param activityCode The mutation being recorded.
 * @param agentReference The acting user, recorded as the performer.
 * @returns The Provenance to create. Returns it unsaved so the caller owns the write and its failure.
 */
export function buildChargeItemProvenance({
  chargeItem,
  activityCode,
  agentReference,
  practiceOrganizationId,
}: BuildChargeItemProvenanceParams): Provenance {
  const resolvedPracticeOrganizationId = practiceOrganizationIdFromChargeItem(
    chargeItem,
    practiceOrganizationId,
  );

  return buildVersionedTargetProvenance({
    resource: chargeItem,
    versionExtensionUrl: Extensions.ProvenanceChargeItemVersionId,
    activity: {
      system: CHARGE_ITEM_PROVENANCE_ACTIVITY_SYSTEM,
      code: activityCode,
      display: CHARGE_ITEM_PROVENANCE_ACTIVITY_DISPLAY[activityCode],
    },
    agentReference,
    practiceOrganizationId: resolvedPracticeOrganizationId,
  });
}

/**
 * Practice that owns the service: the first Organization account on the ChargeItem, else the first
 * Organization in `meta.compartment`, else an explicit id. A ChargeItem belongs to one practice, so
 * one id is returned and the Provenance is filed under that practice alone.
 *
 * @param chargeItem The ChargeItem whose practice is resolved.
 * @param fallbackPracticeOrganizationId Practice to use when the ChargeItem names none.
 * @returns The practice Organization id, or undefined when none is available.
 */
export function practiceOrganizationIdFromChargeItem(
  chargeItem: ChargeItem,
  fallbackPracticeOrganizationId?: string,
): string | undefined {
  const organizationReference =
    extractAccountReferences(chargeItem.meta)?.find(isOrganizationReference) ??
    chargeItem.meta?.compartment?.find(isOrganizationReference);
  const fromChargeItem = resolveId(organizationReference);
  if (fromChargeItem) {
    return fromChargeItem;
  }

  const trimmedFallback = fallbackPracticeOrganizationId?.trim();
  return trimmedFallback ? trimmedFallback : undefined;
}

function isOrganizationReference(reference: Reference): boolean {
  return Boolean(reference.reference?.startsWith("Organization/"));
}

/**
 * Reads the ChargeItem version id stamped on a service Provenance's target.
 *
 * @param provenance The Provenance to read, if there is one.
 * @returns The stamped version id, or undefined when the record predates the extension.
 */
export function getChargeItemProvenanceVersionId(
  provenance: Provenance | undefined,
): string | undefined {
  return getProvenanceTargetVersionId(provenance, Extensions.ProvenanceChargeItemVersionId);
}

/**
 * Reads the mutation a service Provenance records.
 *
 * @param provenance The Provenance to read, if there is one.
 * @returns The activity code, or undefined when the coding is absent or from another system.
 */
export function getChargeItemProvenanceActivityCode(
  provenance: Provenance | undefined,
): ChargeItemProvenanceActivityCode | undefined {
  return getProvenanceActivityCode(
    provenance,
    CHARGE_ITEM_PROVENANCE_ACTIVITY_SYSTEM,
    Object.values(ChargeItemProvenanceActivityCode),
  );
}
